/**
 * Flow device condition/trigger creation.
 * Provides conversion from Flow node data to scene JSON format.
 *
 * Trigger semantics (aligned with shelly_jemms dev_jemm runtime):
 *
 *  executionMode === 'once'
 *    Threshold trigger → _c_trig in the runtime.
 *    Fires once when the property crosses the threshold, then suppressed for `hold_off` seconds.
 *    JSON form: { rr: "dev:id:cmd", thsop: "eq|lt|gt", thsval: value, hold_off?: N }
 *
 *  executionMode === 'any'
 *    Expression trigger evaluated on every property change → _c_trig (expression form).
 *    Fires every time the condition evaluates to true, never suppressed.
 *    JSON form: { "eq|lt|gt": [{ rr: "dev:id:cmd" }, value] }
 *
 *  executionMode === 'repeat'
 *    Same expression form as 'any' but additionally re-triggers on a timer tick.
 *    JSON form: { "eq|lt|gt": [{ rr: "dev:id:cmd" }, value], rrtrig: { rr: "dt:t" } }
 *
 *  Standalone condition (no trigger, uses `r` not `rr`):
 *    Read-only check, does not fire on its own — used inside AND/OR gates.
 *    JSON form: { r: "dev:id:cmd", cmpop: "eq|lt|gt", cmpval: value }
 */

import { convertUiPropertyToSceneCommand, convertUiValueToSceneValue } from './utilities';
import { createHexToDecimalOrId } from 'src/helpers/utilities';

type ConditionOperator = 'eq' | 'ne' | 'lt' | 'gt' | 'le' | 'ge';

export interface FlowDeviceConditionInput {
	deviceId: string;
	channel?: number | string;
	property: string;
	operator: string;
	value: unknown;
	devMode?: string;
}

/** Map Flow / Scenes UI operator strings to IoTS cmpop/thsop (canonical). */
export function toSceneOperator(operator: string): ConditionOperator {
	const op = String(operator ?? '')
		.toLowerCase()
		.trim()
		.replace(/\s+/g, '_');
	if (op === 'less_than' || op === 'lt') return 'lt';
	if (op === 'more_than' || op === 'greater_than' || op === 'gt') return 'gt';
	if (op === 'le' || op === 'lte' || op === 'less_than_or_equal') return 'le';
	if (op === 'ge' || op === 'gte' || op === 'greater_than_or_equal') return 'ge';
	if (op === 'ne' || op === 'neq' || op === 'not_equal') return 'ne';
	return 'eq';
}

/**
 * Build a reactive trigger block for a device property that fires on ANY change.
 * Used as the scene `if` block when a switch node is the root — the actual
 * value matching is done in the `do` block via nested if/do/else (synchronous reads).
 *
 * Uses the expression-trigger form { neq: [{ rr: "..." }, null] } so the JEMMS
 * runtime sets up a reactive subscription and the condition always evaluates to
 * true (any value is neq null), causing the scene to fire on every property change.
 * A bare `rr` without thsop/operator is NOT recognized by the runtime and would
 * silently never fire.
 */
export function buildBareReactiveTrigger(input: FlowDeviceConditionInput): Record<string, unknown> {
	const condRef = buildDeviceConditionRef(input);
	return { neq: [{ rr: condRef }, null], _gui_purpose: 'condition' };
}

export function buildDeviceConditionRef(input: FlowDeviceConditionInput): string {
	const ch = Number(input.channel ?? 0);
	const sceneCommand = convertUiPropertyToSceneCommand(input.property, ch, input.devMode);
	const backEndID = input.deviceId?.toLowerCase().startsWith('x')
		? input.deviceId
		: createHexToDecimalOrId(input.deviceId);
	return `dev:${backEndID}:${sceneCommand}`;
}

export function flowConvertUiValueToScene(
	sceneCommand: string,
	value: unknown,
	operator: ConditionOperator,
): unknown {
	const wantedValue = operator !== 'eq' ? Number(value) : value;
	return convertUiValueToSceneValue(sceneCommand, wantedValue);
}

/**
 * For turn/state properties: keep the natural 'on'/'off' strings so the comparison
 * matches the raw device state reported by the JEMMS extractr at runtime.
 * All Gen1 relays and lights report r1/state as 'on'/'off', NOT '1'/'0'.
 * Using '1'/'0' for cmpop/cmpval or the expression form requires JEMMS to normalise
 * those values before comparing, which is not guaranteed for all evaluation paths.
 *
 * NOTE: the threshold (once) form uses thsop/thsval and JEMMS does normalise there,
 * so that form keeps '1'/'0' for consistency with the Scenes editor.
 */
function toNativeTurnValue(property: string, value: unknown): unknown {
	const prop = String(property).toLowerCase();
	if (prop.match(/^r\d+$/) || prop === 'state' || prop === 'turn' || prop.startsWith('turn')) {
		const v = String(value).toLowerCase();
		if (v === '1' || v === 'on' || v === 'true') return 'on';
		if (v === '0' || v === 'off' || v === 'false') return 'off';
	}
	return value;
}

/** Normalize on/off string values to scene "1"/"0" for threshold (once) triggers. */
function normalizeTurnValue(property: string, value: unknown): unknown {
	const prop = String(property).toLowerCase();
	if (prop === 'turn' || prop === 'state' || prop.startsWith('turn')) {
		const v = String(value).toLowerCase();
		if (v === 'on' || v === 'true') return '1';
		if (v === 'off' || v === 'false') return '0';
	}
	return value;
}

/**
 * Build a standalone device condition (uses `r`, not `rr`).
 * Used for read-only condition checks inside AND/OR gates.
 * Uses native 'on'/'off' values to match JEMMS raw device state.
 */
export function createDeviceConditionFromFlow(input: FlowDeviceConditionInput): Record<string, unknown> {
	const condRef = buildDeviceConditionRef(input);
	const operator = toSceneOperator(input.operator);
	const ch = Number(input.channel ?? 0);
	const sceneCommand = convertUiPropertyToSceneCommand(input.property, ch, input.devMode);
	const sceneValue = flowConvertUiValueToScene(sceneCommand, input.value, operator);
	// Use native 'on'/'off' for turn/state so the comparison matches the raw
	// device state string that JEMMS stores in dev.state[r].
	const value = toNativeTurnValue(input.property, sceneValue);

	return {
		r: condRef,
		cmpop: operator,
		cmpval: value,
		_gui_purpose: 'condition',
	};
}

/**
 * Build a device trigger (uses `rr`) for once/any/repeat execution.
 *
 * - once:   threshold crossing, fires once, suppressed by hold_off
 * - any:    expression form, fires on every matching change
 * - repeat: expression form + rrtrig timer for periodic re-evaluation
 */
export function createDeviceTriggerFromFlow(
	input: FlowDeviceConditionInput,
	executionMode: 'once' | 'any' | 'repeat',
	holdOff?: number,
): Record<string, unknown> {
	const condRef = buildDeviceConditionRef(input);
	const operator = toSceneOperator(input.operator);
	const ch = Number(input.channel ?? 0);
	const sceneCommand = convertUiPropertyToSceneCommand(input.property, ch, input.devMode);
	const sceneValue = flowConvertUiValueToScene(sceneCommand, input.value, operator);

	if (executionMode === 'once') {
		// Threshold trigger: { rr, thsop, thsval, hold_off? }
		// JEMMS thsops normalises 'on'/'off' ↔ '1'/'0', so '1'/'0' is correct here
		// and consistent with Scenes editor output.
		const value = normalizeTurnValue(input.property, sceneValue);
		return {
			rr: condRef,
			thsop: operator,
			thsval: value,
			...(holdOff != null && holdOff > 0 && { hold_off: holdOff }),
			_gui_purpose: 'condition',
		};
	}

	// Expression form: { "eq|lt|gt": [{ rr: condRef }, value] }
	// The {rr} getrp returns the raw device state string ('on'/'off'),
	// so the comparison value must also be 'on'/'off' to avoid a strict-equality mismatch.
	const expressionValue = toNativeTurnValue(input.property, sceneValue);
	const base: Record<string, unknown> = {
		[operator]: [{ rr: condRef }, expressionValue],
		_gui_purpose: 'condition',
	};

	if (executionMode === 'repeat') {
		// Re-trigger on timer tick in addition to property change
		base.rrtrig = { rr: 'dt:t' };
	}

	return base;
}
